import type { SyncPushResult } from '@core/domain/sync/SyncPushResult.ts';

export async function runSyncPushLoop<T extends { publicId: string }, P = T>(opts: {
  pushPort: { push: (rows: P[]) => Promise<SyncPushResult> } | null;
  listPending: () => Promise<T[]>;
  markSynced: (ids: string[]) => Promise<void>;
  markFailed: (id: string, msg: string) => Promise<void>;
  flushPersist?: () => Promise<void>;
  beforePush?: (rows: T[]) => Promise<P[]>;
  afterAccepted?: (pending: T[], accepted: string[], pushResult: SyncPushResult) => Promise<string[] | void>;
}): Promise<string[]> {
  if (!opts.pushPort) return [];
  const pending = await opts.listPending();
  if (pending.length === 0) return [];
  if (!globalThis.navigator?.onLine) {
    throw new Error('Sin conexión: no se pudieron enviar registros.');
  }
  const attemptedIds = pending.map((row) => row.publicId);
  try {
    const payload = opts.beforePush ? await opts.beforePush(pending) : (pending as unknown as P[]);
    const pushResult = await opts.pushPort.push(payload);
    const { acceptedPublicIds, rejectedPublicIds = [] } = pushResult;
    let syncedIds = acceptedPublicIds;
    if (opts.afterAccepted) {
      const resolved = await opts.afterAccepted(pending, acceptedPublicIds, pushResult);
      if (resolved) syncedIds = resolved;
    }
    if (syncedIds.length > 0) await opts.markSynced(syncedIds);
    for (const row of rejectedPublicIds) {
      await opts.markFailed(row.publicId, row.error);
    }
    return syncedIds;
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    for (const id of attemptedIds) {
      await opts.markFailed(id, message);
    }
    throw e instanceof Error ? e : new Error(message);
  } finally {
    if (opts.flushPersist) await opts.flushPersist();
  }
}
